/*
 * Eco, the assistant's frog, from Peter's chat-button kit (eco-chat-buttons-preview.html,
 * 2026-10-29). Defines <eco-avatar>; EcoAvatar.tsx is how React draws it. Changed from the
 * kit: the body is the brand pack's Eco Green, and the mood glows are green, or red for an
 * error or an alert, because the app has no blue or amber (decision 110). Decision 116.
 */
/* Eco - EcoPest Group's frog assistant.
 * A rounded 3D head and two eye domes projected into SVG, with interruptible clips
 * that start and finish on the same resting expression. No dependencies.
 *
 *   <eco-avatar follow state="idle" style="width:96px"></eco-avatar>
 *   el.play('happy')                    one 2.4 s clip, then back to rest
 *   el.play('thinking', { loop: true }) repeats until interrupted
 *   el.stop()                           eases from the current pose to rest
 *   'eco-animation-end' -> event.detail.state
 */
(() => {
 if (!window.customElements || customElements.get('eco-avatar')) return;

 const CX = 160, CY = 158;
 const BODY = '#94C83D';                       // EcoPest logo green; override with --eco-body
 const STATES = ['idle', 'listening', 'thinking', 'talking', 'happy', 'sleeping', 'error', 'alert'];
 const ALIAS = { focus: 'listening', searching: 'thinking', replying: 'talking', responding: 'talking',
   success: 'happy', standby: 'sleeping', warning: 'alert', ready: 'idle', rest: 'idle' };
 const LABEL = { idle: 'ready', listening: 'listening', thinking: 'thinking', talking: 'responding',
   happy: 'happy', sleeping: 'standby', error: 'error', alert: 'alert' };
 const norm = s => { s = String(s || '').toLowerCase(); s = ALIAS[s] || s; return STATES.includes(s) ? s : null; };

 /* ---------------- colour ---------------- */
 const hexRgb = h => { h = h.replace('#', '').trim(); if (h.length === 3) h = h.replace(/./g, c => c + c); const n = parseInt(h, 16); return [n >> 16 & 255, n >> 8 & 255, n & 255]; };
 const rgbHex = c => '#' + c.map(v => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('');
 const mix = (a, b, t) => rgbHex(hexRgb(a).map((v, i) => v + (hexRgb(b)[i] - v) * t));
 function palette(body) {
  if (!/^#?[0-9a-f]{3}([0-9a-f]{3})?$/i.test(String(body || '').trim())) body = BODY;
  body = '#' + String(body).trim().replace('#', '');
  return {
   hi: mix(body, '#f4ffd8', .42), mid: body, low: mix(body, '#0b2414', .30), deep: mix(body, '#071a0e', .55),
   rim: mix(body, '#e8ffd0', .55), belly: mix(body, '#f6f8de', .74), mouth: mix(body, '#10200f', .78),
   lip: mix(body, '#0c1a0b', .62), brow: mix(body, '#0c1a0b', .55), nostril: mix(body, '#0c1a0b', .5),
   shadow: mix(body, '#0c1a0b', .7)
  };
 }

 /* ---------------- geometry ---------------- */
 const createSurface = function createSurface() {
  const rx = 104, ry = 69, rz = 80, n = 2.25, e = 2 / n;
  const signed = (v, p) => Math.sign(v) * Math.pow(Math.abs(v), p);
  const mesh = [];
  for (let j = 0; j <= 20; j++) {
   const v = -Math.PI / 2 + j * Math.PI / 20;
   for (let i = 0; i < 48; i++) {
    const u = i * Math.PI * 2 / 48;
    mesh.push([rx * signed(Math.cos(v), e) * signed(Math.sin(u), e), ry * signed(Math.sin(v), e), rz * signed(Math.cos(v), e) * signed(Math.cos(u), e)]);
   }
  }
  const rot = (p, yaw, pitch) => {
   const a = p[0] * Math.cos(yaw) + p[2] * Math.sin(yaw), z = -p[0] * Math.sin(yaw) + p[2] * Math.cos(yaw);
   return [a, p[1] * Math.cos(pitch) - z * Math.sin(pitch), p[1] * Math.sin(pitch) + z * Math.cos(pitch)];
  };
  const project = (p, yaw, pitch) => { const r = rot(p, yaw, pitch); return [r[0] + CX, r[1] + CY, r[2]]; };
  const surface = (x, y) => { const q = Math.max(.001, 1 - Math.pow(Math.abs(x / rx), n) - Math.pow(Math.abs(y / ry), n)); return [x, y, rz * Math.pow(q, 1 / n)]; };
  function hull(points) {
   const p = points.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
   const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
   const lower = [], upper = [];
   for (const a of p) { while (lower.length > 1 && cross(lower.at(-2), lower.at(-1), a) <= 0) lower.pop(); lower.push(a); }
   for (let i = p.length - 1; i >= 0; i--) { const a = p[i]; while (upper.length > 1 && cross(upper.at(-2), upper.at(-1), a) <= 0) upper.pop(); upper.push(a); }
   return lower.slice(0, -1).concat(upper.slice(0, -1));
  }
  const f = v => v.toFixed(2);
  function smooth(points) {
   const mid = (a, b) => [.5 * (a[0] + b[0]), .5 * (a[1] + b[1])];
   const first = mid(points.at(-1), points[0]);
   let d = `M${f(first[0])} ${f(first[1])}`;
   for (let i = 0; i < points.length; i++) { const p = points[i], m = mid(p, points[(i + 1) % points.length]); d += `Q${f(p[0])} ${f(p[1])} ${f(m[0])} ${f(m[1])}`; }
   return d + 'Z';
  }
  const line = pts => 'M' + pts.map(p => f(p[0]) + ' ' + f(p[1])).join('L');
  const norm3 = v => { const l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l]; };
  const cross3 = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];

  // Eye domes sit on top of the head; each eye is a disc on the front of its dome.
  const DOME_R = 32, EYE_R = 24.5;
  function eyeFrame(side, yaw, pitch) {
   const c = [side * 54, -57, 28];
   const o = norm3([side * .28, -.16, 1]);
   const u = norm3(cross3([0, 1, 0], o)), v = cross3(o, u);
   const centre = [c[0] + o[0] * DOME_R * .55, c[1] + o[1] * DOME_R * .55, c[2] + o[2] * DOME_R * .55];
   const map = (a, b) => project([centre[0] + u[0] * a + v[0] * b, centre[1] + u[1] * a + v[1] * b, centre[2] + u[2] * a + v[2] * b], yaw, pitch);
   return { dome: project(c, yaw, pitch), map };
  }
  function circle(cx, cy, r) { const pts = []; for (let i = 0; i < 36; i++) { const t = i * Math.PI * 2 / 36; pts.push([cx + r * Math.cos(t), cy + r * Math.sin(t)]); } return smooth(pts); }
  function ellipse(map, ox, oy, rx2, ry2, steps = 32) { const pts = []; for (let i = 0; i < steps; i++) { const t = i * Math.PI * 2 / steps; pts.push(map(ox + rx2 * Math.cos(t), oy + ry2 * Math.sin(t))); } return smooth(pts); }
  function onHead(x, y, yaw, pitch) { return project(surface(x, y), yaw, pitch); }

  // Mouth: one cubic edge per lip. round 0 = quadratic-equivalent, 1 = elliptical.
  function lip(w, h, round, t) {
   const k = 4 * h / 3, c1 = -w / 3 + (-w + w / 3) * round, c2 = -c1;
   const mt = 1 - t;
   return [mt * mt * mt * -w + 3 * mt * mt * t * c1 + 3 * mt * t * t * c2 + t * t * t * w, 3 * mt * mt * t * k + 3 * mt * t * t * k];
  }

  function pose(headX, headY, gx, gy, blink, v) {
   headX = Math.max(-1.25, Math.min(1.25, headX + v.turn));
   const yaw = headX * .38, pitch = -headY * .27 + v.nod;
   const shell = hull(mesh.map(p => project(p, yaw, pitch)));
   const out = { body: smooth(shell) };
   const lookX = Math.max(-1, Math.min(1, gx + v.lookX)), lookY = Math.max(-1, Math.min(1, gy + v.lookY));

   const eyes = [-1, 1].map(side => {
    const F = eyeFrame(side, yaw, pitch);
    const sx = v.eyeW, sy = Math.max(.05, v.eyeH * blink);
    const m = (a, b) => F.map(a * sx, b * sy);
    const pa = lookX * EYE_R * .44, pb = lookY * EYE_R * .40;
    const inner = side < 0 ? 1 : -1;                    // toward the middle of the face
    const bo = side < 0 ? v.bLo : v.bRo, bi = side < 0 ? v.bLi : v.bRi, ba = side < 0 ? v.bLa : v.bRa;
    const browY = -EYE_R - 8;
    const brow = [];
    for (let i = 0; i <= 10; i++) {
     const t = i / 10, a = (-15 + 30 * t) * -inner;   // outer -> inner
     const b = browY + bo + (bi - bo) * t - ba * 4 * t * (1 - t);
     brow.push(F.map(a, b));
    }
    const happy = [], closed = [];
    for (let i = 0; i <= 12; i++) {
     const t = i / 12, a = -14 + 28 * t;
     happy.push(F.map(a, 3 - 13 * 4 * t * (1 - t)));
     closed.push(F.map(a, -1 + 8 * 4 * t * (1 - t)));
    }
    return {
     dome: circle(F.dome[0], F.dome[1], DOME_R),
     domeHi: F.dome,
     white: ellipse(m, 0, 0, EYE_R, EYE_R),
     pupil: ellipse(m, pa, pb, 12.6, 13.6, 24),
     glint: ellipse(m, pa - 4.6, pb - 5.4, 3.6, 3.6, 16),
     happy: line(happy), closed: line(closed), brow: line(brow)
    };
   });
   out.eyes = eyes;

   // Mouth on the head surface, wrapped by the projection.
   const W = 56 * v.mW, my = 22 + v.mY;
   const up = [], lo = [];
   for (let i = 0; i <= 18; i++) { const p = lip(W, v.mUp, v.mR, i / 18); up.push(onHead(p[0], my + p[1], yaw, pitch)); }
   for (let i = 18; i >= 0; i--) { const p = lip(W, v.mLo, v.mR, i / 18); lo.push(onHead(p[0], my + p[1], yaw, pitch)); }
   out.mouth = line(up.concat(lo)) + 'Z';
   const open = Math.max(0, v.mLo - v.mUp);
   out.tongueIn = open > .3 ? ellipseOnHead(0, my + v.mLo * .78, W * .42, Math.max(.5, open * .42), yaw, pitch) : 'M0 0Z';
   const mc = onHead(0, my + (v.mUp + v.mLo) * .5, yaw, pitch);
   out.mouthCentre = mc;

   // Chin, cheeks, nostrils.
   const puff = 1 + v.puff;
   out.belly = ellipseOnHead(0, 50 + 4 * v.puff, 78 * (1 + .08 * v.puff), 30 * puff, yaw, pitch);
   out.blush = [-1, 1].map(s => ellipseOnHead(s * 66, 14, 13, 7, yaw, pitch));
   out.nostrils = [-1, 1].map(s => ellipseOnHead(s * 10, -2, 2.6, 1.7, yaw, pitch));
   const light = project(surface(-44, -40), yaw * .3, pitch * .3);
   out.light = light;
   out.yaw = yaw;
   return out;
  }
  function ellipseOnHead(x, y, rx2, ry2, yaw, pitch) {
   const pts = []; for (let i = 0; i < 28; i++) { const t = i * Math.PI * 2 / 28; pts.push(project(surface(x + rx2 * Math.cos(t), y + ry2 * Math.sin(t)), yaw, pitch)); } return smooth(pts);
  }
  return { pose };
 };

 /* ---------------- motion: neutral -> clip -> neutral ---------------- */
 const createMotion = function createMotion() {
  const duration = 2400, exitDuration = 320;
  const rest = () => ({
   turn: 0, nod: 0, tx: 0, ty: 0, rot: 0, sx: 1, sy: 1, lookX: 0, lookY: 0,
   eyeW: 1, eyeH: 1, happy: 0, closed: 0,
   bLo: 0, bLi: 0, bLa: 0, bRo: 0, bRi: 0, bRa: 0, browA: 0,
   mW: 1, mY: 0, mUp: 5, mLo: 5, mR: 0,
   puff: 0, tongue: 0, fly: 0, flyX: 0, flyY: 0, grab: 0,
   process: 0, zz: 0, blush: 0, dim: 0,
   alpha: 0, red: 0, green: 0, blue: 0
  });
  const clamp = v => Math.max(0, Math.min(1, v));
  const ease = v => { v = clamp(v); return v * v * v * (v * (v * 6 - 15) + 10); };
  const bump = (t, a, b) => (t > a && t < b) ? Math.sin(Math.PI * (t - a) / (b - a)) ** 2 : 0;
  const blend = (a, b, t) => Object.fromEntries(Object.keys(a).map(k => [k, a[k] + (b[k] - a[k]) * t]));
  const tints = { listening: [148, 200, 61, .06], thinking: [148, 200, 61, .05], talking: [148, 200, 61, .05], happy: [168, 214, 102, .14],
   error: [185, 28, 28, .16], alert: [185, 28, 28, .12], sleeping: [15, 40, 30, .0] };
  function clip(state, ms) {
   const a = rest(), t = ms / 1000;
   if (ms <= 0 || ms >= duration || state === 'idle') return a;
   const weight = ease(t / .36) * (1 - ease((t - 1.8) / .6));
   let alpha = (tints[state] || [0, 0, 0, 0])[3];
   if (state === 'listening') {
    a.eyeW = a.eyeH = 1.1; a.rot = 5; a.lookY = -.12; a.browA = .55; a.bLo = a.bRo = -2; a.bLi = a.bRi = -5;
    a.mW = .66; a.mUp = 5; a.mLo = 6; a.blush = .35;
   }
   if (state === 'thinking') {
    a.lookX = .55; a.lookY = -.62; a.browA = .7; a.bRo = -7; a.bRa = 1.2; a.bLo = 2; a.bLi = 1;
    a.mW = .5; a.mUp = 1; a.mLo = 1.4; a.mY = 1; a.process = 1; a.turn = .12; a.nod = -.04;
   }
   if (state === 'talking') {
    const s = Math.sin(t * 12.5), s2 = Math.sin(t * 7.3);
    a.mW = .82 + .05 * s2; a.mUp = 2.5 + .6 * s; a.mLo = 9 + 4.5 * s + 2 * s2; a.mR = .35;
    a.puff = .1 + .07 * Math.sin(t * 6.2); a.ty = -1.2 * Math.sin(t * 6.2); a.browA = .25; a.bLi = a.bRi = -2; a.blush = .2;
   }
   if (state === 'happy') {
    // A fly drifts in, the tongue catches it, then two happy hops.
    a.fly = ease((t - .25) / .35) * (1 - ease((t - 1.05) / .3));
    a.flyX = 6 * Math.sin(t * 4.1); a.flyY = 5 * Math.sin(t * 6.3);
    const reach = bump(t, .62, 1.38);
    a.tongue = reach; a.grab = ease((t - 1.0) / .06);
    a.lookX = .6 * (1 - ease((t - 1.05) / .25)); a.lookY = -.35 * (1 - ease((t - 1.05) / .25));
    a.mLo = 4 + 10 * reach; a.mUp = 4 - 2 * reach; a.mR = .4 * reach;
    const joy = ease((t - 1.2) / .22);
    a.happy = joy; a.blush = .55 * joy;
    a.mLo += 9 * joy; a.mW = 1 + .1 * joy; a.mUp += 2 * joy; a.mR += .5 * joy;
    a.ty = -14 * bump(t, 1.32, 1.72) - 7 * bump(t, 1.72, 2.08);
    a.sy = 1 + .03 * bump(t, 1.28, 1.42) - .03 * bump(t, 1.42, 1.62);
   }
   if (state === 'sleeping') {
    a.closed = 1; a.dim = .32; a.ty = 5; a.rot = -3; a.nod = .05; a.mW = .42; a.mUp = 1; a.mLo = 1.5;
    a.sy = 1 - .012 * (1 + Math.sin(t * 2.6)); a.zz = 1;
   }
   if (state === 'error' || state === 'alert') {
    const h = t - .3, env = ease(h / .15) * (1 - ease((h - .85) / .5));
    const pulse = h > 0 && h < 1.6 ? Math.sin(h * Math.PI / .8) ** 2 : 0;
    if (state === 'error') {
     a.turn = .38 * Math.sin(h * Math.PI * 5) * env; a.browA = .95; a.bLi = a.bRi = -7; a.bLo = a.bRo = 3; a.bLa = a.bRa = -.4;
     a.mW = .6; a.mUp = -5; a.mLo = -3.5; a.mY = 3; a.eyeH = .9;
     alpha += pulse * .2;
    } else {
     a.tx = 1.8 * Math.sin(h * Math.PI * 18) * env; a.turn = .1 * Math.sin(h * Math.PI * 14) * env;
     a.eyeW = a.eyeH = 1.2; a.browA = .9; a.bLo = a.bRo = -6; a.bLi = a.bRi = -8;
     a.mW = .3; a.mR = 1; a.mUp = -6; a.mLo = 9; a.mY = 2;
     alpha += pulse * .18;
    }
   }
   const c = tints[state] || [0, 0, 0, 0];
   a.alpha = alpha; a.red = c[0] * alpha; a.green = c[1] * alpha; a.blue = c[2] * alpha;   // premultiplied
   return blend(rest(), a, weight);
  }
  let state = 'idle', started = 0, from = rest(), exit = 0, loop = false;
  function sample(now) {
   const elapsed = Math.max(0, now - started);
   if (elapsed < exit) return { value: blend(from, rest(), ease(elapsed / exit)), phase: 'transition', done: false, state };
   const local = elapsed - exit;
   if (state === 'idle') return { value: rest(), phase: 'rest', done: true, state };
   if (!loop && local >= duration) return { value: rest(), phase: 'rest', done: true, state };
   return { value: clip(state, loop ? local % duration : local), phase: 'playing', done: false, state };
  }
  function play(next, now, options = {}) {
   from = sample(now).value;
   const neutral = rest(); exit = Object.keys(from).some(k => Math.abs(from[k] - neutral[k]) > 1e-8) ? exitDuration : 0;
   state = next; started = now; loop = Boolean(options.loop) && next !== 'idle';
   return sample(now);
  }
  function still(name) {
   // A readable static expression for reduced motion and exported artwork.
   if (name === 'idle') return rest();
   const v = clip(name, name === 'happy' ? 1500 : name === 'talking' ? 1130 : 1000);
   v.tx = 0; v.ty = 0; v.sy = 1; v.tongue = 0; v.fly = 0; v.grab = 0; v.turn = name === 'thinking' ? v.turn : 0;
   const c = tints[name]; if (c) { v.alpha = c[3]; v.red = c[0] * c[3]; v.green = c[1] * c[3]; v.blue = c[2] * c[3]; }
   return v;
  }
  return { rest, clip, play, sample, still, duration, exitDuration };
 };

 /* ---------------- markup ---------------- */
 let serial = 0;
 const CSS = `
.eco-svg{width:100%;height:100%;overflow:visible}
.eco-float{animation:eco-breathe 11.3s ease-in-out infinite}
.eco-shadow{transform-origin:160px 258px;animation:eco-ground 11.3s ease-in-out infinite}
.eco-fly .eco-wing{transform-box:fill-box;transform-origin:50% 100%;animation:eco-flap .09s linear infinite alternate}
.eco-process circle{animation:eco-signal 1.4s ease-in-out infinite}.eco-process circle:nth-child(2){animation-delay:.18s}.eco-process circle:nth-child(3){animation-delay:.36s}
.eco-zz text{animation:eco-zz 2.4s ease-in-out infinite}.eco-zz text:nth-child(2){animation-delay:.6s}
.eco-svg[data-motion="off"] *{animation:none!important;transition:none!important}
.eco-svg[data-paused] *{animation-play-state:paused!important}
@keyframes eco-breathe{0%,100%{transform:translateY(0)}31%{transform:translateY(-2.4px)}73%{transform:translateY(-.6px)}}
@keyframes eco-ground{0%,100%{opacity:.62;transform:scaleX(1)}31%{opacity:.5;transform:scaleX(.95)}73%{opacity:.58;transform:scaleX(.985)}}
@keyframes eco-flap{from{transform:scaleY(1)}to{transform:scaleY(.35)}}
@keyframes eco-signal{0%,100%{opacity:.3}42%{opacity:1}}
@keyframes eco-zz{0%{opacity:0;transform:translate(0,4px)}40%{opacity:1}100%{opacity:0;transform:translate(6px,-10px)}}
@media(prefers-reduced-motion:reduce){.eco-svg *{animation:none!important;transition:none!important}}`;

 function markup(id, pal) {
  const P = 'eco' + id + '-';
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 300" class="eco-svg" data-state="idle" data-motion="on" role="img" aria-label="Eco, EcoPest assistant - ready"><style>${CSS}</style>
<defs>
<radialGradient id="${P}body" gradientUnits="userSpaceOnUse" cx="118" cy="92" r="205"><stop class="s-hi" stop-color="${pal.hi}"/><stop class="s-mid" offset=".38" stop-color="${pal.mid}"/><stop class="s-low" offset=".74" stop-color="${pal.low}"/><stop class="s-deep" offset="1" stop-color="${pal.deep}"/></radialGradient>
<linearGradient id="${P}rim" x1="0" y1="0" x2="1" y2=".7"><stop class="s-rim" stop-color="${pal.rim}" stop-opacity=".7"/><stop offset=".45" stop-color="${pal.rim}" stop-opacity=".1"/><stop offset="1" stop-color="${pal.deep}" stop-opacity=".35"/></linearGradient>
<radialGradient id="${P}belly" cx=".5" cy=".42" r=".6"><stop class="s-belly" stop-color="${pal.belly}" stop-opacity=".95"/><stop class="s-belly2" offset=".75" stop-color="${pal.belly}" stop-opacity=".55"/><stop class="s-belly3" offset="1" stop-color="${pal.belly}" stop-opacity="0"/></radialGradient>
<radialGradient id="${P}spec"><stop stop-color="#ffffff" stop-opacity=".42"/><stop offset=".4" stop-color="#ffffff" stop-opacity=".14"/><stop offset="1" stop-color="#ffffff" stop-opacity="0"/></radialGradient>
<radialGradient id="${P}shadow"><stop class="s-shadow" stop-color="${pal.shadow}" stop-opacity=".38"/><stop offset="1" stop-color="${pal.shadow}" stop-opacity="0"/></radialGradient>
<radialGradient id="${P}eye" cx=".4" cy=".35" r=".75"><stop stop-color="#ffffff"/><stop offset=".8" stop-color="#f3f7ef"/><stop offset="1" stop-color="#dfe8d8"/></radialGradient>
<clipPath id="${P}clip"><path class="eco-clip"/></clipPath>
<clipPath id="${P}mclip"><path class="eco-mclip"/></clipPath>
<clipPath id="${P}eclip0"><path class="eco-eclip"/></clipPath>
<clipPath id="${P}eclip1"><path class="eco-eclip"/></clipPath>
</defs>
<ellipse class="eco-shadow" cx="160" cy="258" rx="86" ry="12" fill="url(#${P}shadow)"/>
<g class="eco-expression"><g class="eco-float">
<path class="eco-dome" fill="url(#${P}body)" stroke="url(#${P}rim)" stroke-width="1.1"/><path class="eco-dome" fill="url(#${P}body)" stroke="url(#${P}rim)" stroke-width="1.1"/>
<path class="eco-body" fill="url(#${P}body)" stroke="url(#${P}rim)" stroke-width="1.1"/>
<g clip-path="url(#${P}clip)"><path class="eco-belly" fill="url(#${P}belly)"/><ellipse class="eco-spec" rx="56" ry="34" fill="url(#${P}spec)"/>
<path class="eco-blush" fill="#ff8f6b" opacity="0"/><path class="eco-blush" fill="#ff8f6b" opacity="0"/></g>
<ellipse class="eco-dome-hi" rx="11" ry="7" fill="#ffffff" opacity=".28"/><ellipse class="eco-dome-hi" rx="11" ry="7" fill="#ffffff" opacity=".28"/>
<g class="eco-tint" opacity="0"><path class="eco-t"/><path class="eco-t"/><path class="eco-t"/></g>
<g class="eco-dim" fill="#081a10" opacity="0"><path class="eco-t"/><path class="eco-t"/><path class="eco-t"/></g>
<g class="eco-eye"><path class="eco-white" fill="url(#${P}eye)"/><g clip-path="url(#${P}eclip0)"><path class="eco-pupil" fill="#13261a"/><path class="eco-glint" fill="#ffffff"/></g>
<path class="eco-happy" fill="none" stroke="#13261a" stroke-width="5" stroke-linecap="round" opacity="0"/><path class="eco-closed" fill="none" stroke="#13261a" stroke-width="4.2" stroke-linecap="round" opacity="0"/>
<path class="eco-brow" fill="none" stroke="${pal.brow}" stroke-width="4.4" stroke-linecap="round" opacity="0"/></g>
<g class="eco-eye"><path class="eco-white" fill="url(#${P}eye)"/><g clip-path="url(#${P}eclip1)"><path class="eco-pupil" fill="#13261a"/><path class="eco-glint" fill="#ffffff"/></g>
<path class="eco-happy" fill="none" stroke="#13261a" stroke-width="5" stroke-linecap="round" opacity="0"/><path class="eco-closed" fill="none" stroke="#13261a" stroke-width="4.2" stroke-linecap="round" opacity="0"/>
<path class="eco-brow" fill="none" stroke="${pal.brow}" stroke-width="4.4" stroke-linecap="round" opacity="0"/></g>
<path class="eco-nostril" fill="${pal.nostril}" opacity=".75"/><path class="eco-nostril" fill="${pal.nostril}" opacity=".75"/>
<path class="eco-mouth" fill="${pal.mouth}" stroke="${pal.lip}" stroke-width="3.4" stroke-linejoin="round" stroke-linecap="round"/>
<g clip-path="url(#${P}mclip)"><path class="eco-tongue-in" fill="#f0788a"/></g>
<path class="eco-tongue" fill="none" stroke="#ef6f84" stroke-width="8" stroke-linecap="round" opacity="0"/><circle class="eco-tongue-tip" r="7" fill="#f47d91" opacity="0"/>
<g class="eco-process" fill="${pal.mid}" opacity="0"><circle cx="232" cy="64" r="5"/><circle cx="249" cy="49" r="6.5"/><circle cx="269" cy="31" r="8.5"/></g>
<g class="eco-zz" fill="${pal.low}" font-family="Arial,Helvetica,sans-serif" font-weight="700" opacity="0"><text x="236" y="70" font-size="20">z</text><text x="254" y="50" font-size="15">z</text></g>
</g>
<g class="eco-fly" opacity="0"><g transform="scale(1.35)"><ellipse class="eco-wing" cx="-3.5" cy="-6" rx="4.6" ry="7.6" fill="#e3f1f8" stroke="#8aa7b5" stroke-width=".7" transform="rotate(-28)"/><ellipse class="eco-wing" cx="3.5" cy="-6" rx="4.6" ry="7.6" fill="#e3f1f8" stroke="#8aa7b5" stroke-width=".7" transform="rotate(28)"/><ellipse rx="6.2" ry="4.6" fill="#22302a"/><circle cx="5.4" cy="-.8" r="2.8" fill="#8a2f2f"/></g></g></g>
</svg>`;
 }

 const surface = createSurface();
 const f2 = v => (+v).toFixed(3);

 function bind(svg) {
  const q = s => svg.querySelector(s), qa = s => [...svg.querySelectorAll(s)];
  return { svg, body: q('.eco-body'), clip: q('.eco-clip'), domes: qa('.eco-dome'), domeHi: qa('.eco-dome-hi'), belly: q('.eco-belly'), spec: q('.eco-spec'),
   blush: qa('.eco-blush'), tint: q('.eco-tint'), tintP: qa('.eco-tint .eco-t'), dim: q('.eco-dim'), dimP: qa('.eco-dim .eco-t'),
   eyes: qa('.eco-eye').map((g, i) => ({ g, white: g.querySelector('.eco-white'), clip: qa('.eco-eclip')[i], pupil: g.querySelector('.eco-pupil'), glint: g.querySelector('.eco-glint'), happy: g.querySelector('.eco-happy'), closed: g.querySelector('.eco-closed'), brow: g.querySelector('.eco-brow') })),
   nostrils: qa('.eco-nostril'), mouth: q('.eco-mouth'), mclip: q('.eco-mclip'), tongueIn: q('.eco-tongue-in'), tongue: q('.eco-tongue'), tip: q('.eco-tongue-tip'),
   process: q('.eco-process'), zz: q('.eco-zz'), expression: q('.eco-expression'), shadow: q('.eco-shadow'), fly: q('.eco-fly') };
 }

 const FLY_HOME = [292, 150];
 function draw(n, headX, headY, gx, gy, blink, v) {
  const s = surface.pose(headX, headY, gx, gy, blink, v);
  n.body.setAttribute('d', s.body); n.clip.setAttribute('d', s.body);
  s.eyes.forEach((e, i) => {
   n.domes[i].setAttribute('d', e.dome);
   n.domeHi[i].setAttribute('cx', f2(e.domeHi[0] - 10)); n.domeHi[i].setAttribute('cy', f2(e.domeHi[1] - 15));
   const E = n.eyes[i], open = Math.max(0, 1 - Math.max(v.happy, v.closed));
   E.white.setAttribute('d', e.white); E.clip.setAttribute('d', e.white);
   E.pupil.setAttribute('d', e.pupil); E.glint.setAttribute('d', e.glint);
   E.white.setAttribute('opacity', f2(open)); E.pupil.setAttribute('opacity', f2(open)); E.glint.setAttribute('opacity', f2(open));
   E.happy.setAttribute('d', e.happy); E.happy.setAttribute('opacity', f2(Math.max(0, v.happy - v.closed)));
   E.closed.setAttribute('d', e.closed); E.closed.setAttribute('opacity', f2(v.closed));
   E.brow.setAttribute('d', e.brow); E.brow.setAttribute('opacity', f2(Math.max(0, Math.min(1, v.browA))));
  });
  n.tintP[0].setAttribute('d', s.eyes[0].dome); n.tintP[1].setAttribute('d', s.eyes[1].dome); n.tintP[2].setAttribute('d', s.body);
  n.dimP.forEach((p, i) => p.setAttribute('d', n.tintP[i].getAttribute('d')));
  n.belly.setAttribute('d', s.belly);
  n.spec.setAttribute('cx', f2(s.light[0])); n.spec.setAttribute('cy', f2(s.light[1]));
  s.blush.forEach((d, i) => { n.blush[i].setAttribute('d', d); n.blush[i].setAttribute('opacity', f2(.5 * Math.max(0, v.blush))); });
  s.nostrils.forEach((d, i) => n.nostrils[i].setAttribute('d', d));
  n.mouth.setAttribute('d', s.mouth); n.mclip.setAttribute('d', s.mouth); n.tongueIn.setAttribute('d', s.tongueIn);

  // Tongue flick toward the fly; the fly rides the tongue home once caught.
  const mc = s.mouthCentre, fly = [FLY_HOME[0] + v.flyX, FLY_HOME[1] + v.flyY];
  const reach = Math.max(0, Math.min(1, v.tongue));
  const tip = [mc[0] + (fly[0] - mc[0]) * reach, mc[1] + (fly[1] - mc[1]) * reach];
  const ctrl = [(mc[0] + tip[0]) / 2 + 10 * reach, (mc[1] + tip[1]) / 2 + 22 * reach];
  n.tongue.setAttribute('d', `M${f2(mc[0])} ${f2(mc[1])}Q${f2(ctrl[0])} ${f2(ctrl[1])} ${f2(tip[0])} ${f2(tip[1])}`);
  const tv = reach > .02 ? Math.min(1, reach * 6) : 0;
  n.tongue.setAttribute('opacity', f2(tv)); n.tip.setAttribute('opacity', f2(tv));
  n.tip.setAttribute('cx', f2(tip[0])); n.tip.setAttribute('cy', f2(tip[1]));
  const g = Math.max(0, Math.min(1, v.grab)), fx = fly[0] + (tip[0] - fly[0]) * g, fy = fly[1] + (tip[1] - fly[1]) * g;
  n.fly.setAttribute('transform', `translate(${f2(fx)} ${f2(fy)}) scale(${f2(1 - .35 * g)})`);
  n.fly.setAttribute('opacity', f2(Math.max(0, Math.min(1, v.fly))));

  n.process.setAttribute('opacity', f2(v.process)); n.zz.setAttribute('opacity', f2(v.zz));
  const alpha = Math.max(0, v.alpha), rgb = alpha > 1e-8 ? [v.red, v.green, v.blue].map(c => Math.round(Math.max(0, Math.min(255, c / alpha)))) : [255, 255, 255];
  n.tint.setAttribute('fill', 'rgb(' + rgb.join(',') + ')'); n.tint.setAttribute('opacity', f2(alpha));
  n.dim.setAttribute('opacity', f2(Math.max(0, v.dim)));
  const base = 236;
  n.expression.setAttribute('transform', `translate(${f2(v.tx)} ${f2(v.ty)}) rotate(${f2(v.rot)} 160 ${base}) translate(160 ${base}) scale(${f2(v.sx)} ${f2(v.sy)}) translate(-160 -${base})`);
  n.shadow.setAttribute('rx', f2(86 + v.ty * .6));
 }

 const motionOf = createMotion;
 class EcoAvatar extends HTMLElement {
  static get observedAttributes() { return ['state', 'motion', 'follow', 'track', 'loop', 'style']; }
  static get states() { return STATES.slice(); }
  /** Standalone SVG of a state's resting expression (for icons, exports and no-JS fallbacks). */
  static artwork(state = 'idle', options = {}) {
   const name = norm(state) || 'idle';
   const tpl = document.createElement('template');
   tpl.innerHTML = markup('x' + (++serial), palette(options.body || BODY));
   const svg = tpl.content.firstElementChild;
   const m = createMotion();
   draw(bind(svg), 0, 0, 0, 0, 1, m.still(name));
   svg.setAttribute('data-state', name);
   svg.setAttribute('aria-label', 'Eco, EcoPest assistant - ' + LABEL[name]);
   svg.querySelectorAll('[opacity="0.000"]').forEach(el => { if (!el.matches('.eco-tint,.eco-dim')) el.setAttribute('opacity', '0'); });
   return svg.outerHTML;
  }
  constructor() {
   super();
   this.attachShadow({ mode: 'open' });
   this.pal = palette(BODY);
   this.shadowRoot.innerHTML = '<style>:host{display:inline-block;width:160px;aspect-ratio:320/300;line-height:0;vertical-align:middle}:host([hidden]){display:none}</style>' + markup(++serial, this.pal);
   this.svg = this.shadowRoot.querySelector('svg'); this.svg.dataset.live = '';
   this.nodes = bind(this.svg);
   this.media = matchMedia('(prefers-reduced-motion: reduce)');
   this.motion = createMotion(); this.clock = 0; this.playing = false; this.setting = false;
   this.p = { x: 0, y: 0, vx: 0, vy: 0, gx: 0, gy: 0, tx: 0, ty: 0 }; this.visible = true; this.active = true; this.frame = 0; this.last = 0; this.blinkAt = 0; this.blinkTwice = false;
   this.onPointer = e => {
    if (e.pointerType === 'touch' || !this.canMove || !this.visible || !this.active || !this.follows) return;
    const r = this.getBoundingClientRect(); if (!r.width) return;
    const reach = Math.max(115, Math.min(230, r.width * .65));
    this.p.tx = Math.tanh((e.clientX - r.left - r.width / 2) / reach); this.p.ty = Math.tanh((e.clientY - r.top - r.height * .45) / reach); this.start();
   };
   this.onLeave = () => this.returnHome(); this.onMotion = () => this.sync();
   this.onVisibility = () => { this.active = !document.hidden; this.sync(); };
   this.tick = t => {
    this.frame = 0; if (!this.canMove || !this.visible || !this.active) return;
    const dt = Math.min((t - (this.last || t - 16.67)) / 1000, .05); this.last = t; this.clock += dt * 1000; const p = this.p;
    const eyeEase = 1 - Math.exp(-22 * dt); p.gx += (p.tx - p.gx) * eyeEase; p.gy += (p.ty - p.gy) * eyeEase;
    p.vx += (65 * (p.tx - p.x) - 16 * p.vx) * dt; p.vy += (65 * (p.ty - p.y) - 16 * p.vy) * dt; p.x += p.vx * dt; p.y += p.vy * dt;
    let blink = 1;
    if (this.blinkAt) {
     const phase = (t - this.blinkAt) / 165;
     if (phase >= 1) { if (this.blinkTwice) { this.blinkTwice = false; this.blinkAt = t + 70; } else this.blinkAt = 0; }
     else if (phase > 0) blink = 1 - .94 * Math.sin(Math.PI * phase) ** 2;
    }
    const sample = this.motion.sample(this.clock); this.render(blink, sample.value); this.svg.dataset.phase = sample.phase;
    if (this.playing && sample.done) this.finish();
    const movement = Math.abs(p.tx - p.x) + Math.abs(p.ty - p.y) + Math.abs(p.vx) + Math.abs(p.vy) + Math.abs(p.tx - p.gx) + Math.abs(p.ty - p.gy);
    if (movement > .001 || this.blinkAt || this.playing || sample.phase === 'transition') { if (!this.frame) this.frame = requestAnimationFrame(this.tick); } else this.last = 0;
   };
  }
  connectedCallback() {
   this.applyColour();
   this.active = !document.hidden; this.sync();
   window.addEventListener('pointermove', this.onPointer, { passive: true }); window.addEventListener('blur', this.onLeave);
   document.documentElement.addEventListener('pointerleave', this.onLeave); document.addEventListener('visibilitychange', this.onVisibility);
   this.media.addEventListener('change', this.onMotion);
   this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.sync(); }); this.observer.observe(this);
   if (this.hasAttribute('state') && this.state !== 'idle' && !this.playing) this.play(this.state, { loop: this.hasAttribute('loop') });
  }
  disconnectedCallback() {
   window.removeEventListener('pointermove', this.onPointer); window.removeEventListener('blur', this.onLeave);
   document.documentElement.removeEventListener('pointerleave', this.onLeave); document.removeEventListener('visibilitychange', this.onVisibility);
   this.media.removeEventListener('change', this.onMotion); this.observer?.disconnect(); this.stopFrames(); clearTimeout(this.stillTimer);
  }
  attributeChangedCallback(name, old, value) {
   if (!this.svg || this.setting) return;
   if (name === 'style') { this.applyColour(); return; }
   if (name === 'state' && old !== value) this.play(this.state, { loop: this.hasAttribute('loop') });
   else this.sync();
  }
  get state() { return norm(this.getAttribute('state')) || 'idle'; }
  get follows() { return this.hasAttribute('follow') || this.hasAttribute('track'); }
  get canMove() { return this.getAttribute('motion') !== 'off' && !this.media.matches; }
  applyColour() {
   const c = getComputedStyle(this).getPropertyValue('--eco-body').trim();
   const pal = palette(c || BODY); if (JSON.stringify(pal) === JSON.stringify(this.pal)) return; this.pal = pal;
   const set = (sel, attr, val) => this.svg.querySelectorAll(sel).forEach(el => el.setAttribute(attr, val));
   set('.s-hi', 'stop-color', pal.hi); set('.s-mid', 'stop-color', pal.mid); set('.s-low', 'stop-color', pal.low); set('.s-deep', 'stop-color', pal.deep);
   set('.s-rim', 'stop-color', pal.rim); set('.s-belly,.s-belly2,.s-belly3', 'stop-color', pal.belly); set('.s-shadow', 'stop-color', pal.shadow);
   set('.eco-brow', 'stroke', pal.brow); set('.eco-nostril', 'fill', pal.nostril); set('.eco-mouth', 'fill', pal.mouth); set('.eco-mouth', 'stroke', pal.lip);
   set('.eco-process', 'fill', pal.mid); set('.eco-zz', 'fill', pal.low);
  }
  setAttributeQuietly(state) { this.setting = true; this.setAttribute('state', state); this.setting = false; }
  setState(state, options = {}) { return this.play(state, options); }
  play(state, options = {}) {
   const name = norm(state); if (!name) throw new TypeError('Unknown Eco state: ' + state);
   clearTimeout(this.stillTimer);
   this.motion.play(name, this.clock, options); this.playing = name !== 'idle' || this.motion.sample(this.clock).phase === 'transition';
   this.looping = Boolean(options.loop) && name !== 'idle';
   this.setAttributeQuietly(name); this.current = name;
   if (!this.canMove) {
    this.renderStill(name);
    if (name !== 'idle' && !this.looping) this.stillTimer = setTimeout(() => { this.stillTimer = 0; this.renderStill('idle'); this.finish(name); }, this.motion.duration);
    else if (name === 'idle') this.playing = false;
    return this;
   }
   this.sync(); return this;
  }
  stop() { return this.play('idle'); }
  finish(completed = this.current) {
   this.playing = false; this.looping = false; this.setAttributeQuietly('idle'); this.svg.dataset.state = 'idle'; this.current = 'idle';
   this.svg.setAttribute('aria-label', 'Eco, EcoPest assistant - ready');
   this.dispatchEvent(new CustomEvent('eco-animation-end', { detail: { state: completed }, bubbles: true }));
  }
  render(blink = 1, value = this.motion.sample(this.clock).value) {
   this.currentBlink = blink; const p = this.p;
   draw(this.nodes, p.x, p.y, p.gx, p.gy, blink, value);
  }
  renderStill(name) {
   this.svg.dataset.phase = 'still'; this.svg.dataset.state = name; this.svg.dataset.motion = 'off';
   this.svg.setAttribute('aria-label', 'Eco, EcoPest assistant - ' + LABEL[name]);
   draw(this.nodes, 0, 0, 0, 0, 1, this.motion.still(name));
  }
  start() { if (!this.frame && this.canMove && this.visible && this.active) { this.last = 0; this.frame = requestAnimationFrame(this.tick); } }
  returnHome() { this.p.tx = 0; this.p.ty = 0; this.start(); }
  stopFrames() { cancelAnimationFrame(this.frame); this.frame = 0; this.last = 0; clearTimeout(this.blinkTimer); this.blinkTimer = 0; this.blinkAt = 0; }
  scheduleBlink() {
   if (this.blinkTimer || !this.isConnected || !this.canMove || !this.visible || !this.active) return;
   this.blinkTimer = setTimeout(() => { this.blinkTimer = 0; this.blinkAt = performance.now(); this.blinkTwice = Math.random() < .18; this.start(); this.scheduleBlink(); }, 2600 + Math.random() * 4000);
  }
  sync() {
   const name = this.current || this.state;
   this.svg.dataset.state = name; this.svg.dataset.motion = this.canMove ? 'on' : 'off';
   this.svg.setAttribute('aria-label', 'Eco, EcoPest assistant - ' + LABEL[name]);
   this.svg.toggleAttribute('data-paused', !this.active || !this.visible);
   if (!this.canMove) {
    this.stopFrames(); this.p = { x: 0, y: 0, vx: 0, vy: 0, gx: 0, gy: 0, tx: 0, ty: 0 };
    this.renderStill(this.playing || this.looping ? name : 'idle');
    if (this.playing && !this.looping && !this.stillTimer) this.stillTimer = setTimeout(() => { this.stillTimer = 0; this.renderStill('idle'); this.finish(name); }, this.motion.duration);
    return;
   }
   if (this.svg.dataset.phase === 'still') { this.svg.dataset.phase = 'rest'; if (this.stillTimer) { clearTimeout(this.stillTimer); this.stillTimer = 0; } if (this.playing) this.motion.play(name, this.clock, { loop: this.looping }); }
   this.svg.dataset.phase = this.motion.sample(this.clock).phase;
   if (!this.active || !this.visible) { this.stopFrames(); return; }
   if (!this.follows) this.returnHome();
   this.render(this.currentBlink ?? 1); this.scheduleBlink(); this.start();
  }
 }
 EcoAvatar.createMotion = motionOf;
 customElements.define('eco-avatar', EcoAvatar);
})();
